import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { countryDisplayName } from '@/lib/countries';
import type { AddressViewModel } from '@/models/patient';

interface AddressRowProps {
  readonly address: AddressViewModel;
  readonly onEdit: () => void;
  readonly onDelete: () => void;
}

export function AddressRow({ address, onEdit, onDelete }: AddressRowProps): ReactElement {
  const { t } = useTranslation();
  const summary = [
    address.line,
    address.line2,
    address.city,
    address.district,
    address.province,
    address.postalCode,
    countryDisplayName(address.country),
  ]
    .filter((p): p is string => p !== null && p.length > 0)
    .join(', ');

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onEdit();
        }
      }}
      className="group flex items-center gap-3 cursor-pointer py-2 border-b border-dashed border-border last:border-b-0 hover:bg-brand-soft/40 transition-colors -mx-1 px-1"
    >
      <span className="text-[13px] text-foreground flex-1 break-words">
        {summary.length > 0 ? summary : (
          <span className="italic text-muted-foreground">
            {t('patient.demographics.unfilledAddress')}
          </span>
        )}
      </span>
      <span className="flex items-center gap-0.5 shrink-0 opacity-70 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <Button
          size="sm"
          variant="ghost"
          onClick={(event) => {
            event.stopPropagation();
            onEdit();
          }}
          aria-label={t('patient.demographics.editAddress')}
          className="h-7 w-7 p-0"
        >
          <Pencil className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
          aria-label={t('patient.demographics.deleteAddress')}
          className="h-7 w-7 p-0"
        >
          <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden="true" />
        </Button>
      </span>
    </div>
  );
}
